import { useEffect, useRef, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { ApiHelper } from "../../utils/ApiHelper";
import { COMPANIES_DATA } from "../../lib/API_KEYS";
import MultiSelectFilter from "../Common/MultiSelectFilter";
import SearchBar from "../Common/SearchBar";
import Pagination from "../Common/Pagination";

const STATUS_COLORS = {
  "In Review": { bg: "#F3E8FF", color: "#6B21A8", dot: "#6B21A8" },
  "Query Raised": { bg: "#FFF3CD", color: "#F57C00", dot: "#FF9800" },
  Approved: { bg: "#DCFCE7", color: "#166534", dot: "#16A34A" },
  Rejected: { bg: "#FEE2E2", color: "#991B1B", dot: "#DC2626" },
  "On Hold": { bg: "#E5E7EB", color: "#374151", dot: "#6B7280" },
};

export const formatCreated = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "-";
  const date = d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const time = d
    .toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: true })
    .toLowerCase();
  return `${date}\n${time}`;
};

const StatusPill = ({ status }) => {
  const c = STATUS_COLORS[status] || {};
  return (
    <span
      className="px-3 py-1 rounded-pill d-inline-flex align-items-center fw-semibold"
      style={{ backgroundColor: c.bg, color: c.color, fontSize: "0.85rem" }}
    >
      <span
        className="me-2 rounded-circle"
        style={{
          width: "8px",
          height: "8px",
          backgroundColor: c.dot,
          display: "inline-block",
        }}
      />
      {status}
    </span>
  );
};


export default function CompanyListTable({
  type,
  title,
  emptyLabel,
  columns,
}) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(["In Review"]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const smartDefaultAppliedRef = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const statusParam = statusFilter.includes("All")
          ? undefined
          : { status: statusFilter };
        const searchParam = search ? { search } : undefined;

        const res = await new ApiHelper().FetchFromServer(
          ...COMPANIES_DATA,
          {
            type,
            page: currentPage,
            limit: perPage,
            ...statusParam,
            ...searchParam,
          }
        );

        const results = res.data?.results || [];
        const allStatuses = Array.isArray(res.data?.availableStatuses)
          ? res.data.availableStatuses
          : [];

        setData(results);
        setTotalPages(res.data?.totalPages || 1);
        setTotalResults(res.data?.totalResults || 0);
        if (allStatuses.length) setAvailableStatuses(allStatuses);

        // One-time smart default: when landing on "In Review" with no matches,
        // fall back to "Approved" (or "All") so the user lands somewhere useful.
        if (
          !smartDefaultAppliedRef.current &&
          statusFilter.length === 1 &&
          statusFilter[0] === "In Review" &&
          (res.data?.totalResults || 0) === 0 &&
          allStatuses.length
        ) {
          smartDefaultAppliedRef.current = true;
          setStatusFilter(allStatuses.includes("Approved") ? ["Approved"] : ["All"]);
        }
      } catch (err) {
        console.error(`Error fetching ${emptyLabel}:`, err);
        setData([]);
        setTotalPages(1);
        setTotalResults(0);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [type, currentPage, perPage, statusFilter, search, emptyLabel]);

  const statusOptions = ["All", ...availableStatuses];
  const startIdx = (currentPage - 1) * perPage;
  const colSpan = columns.length + 2; // # + columns + Status

  return (
    <div className="container mt-4 border rounded p-3 admin-company-table">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h5 className="fw-bold">{title}</h5>
        <div className="d-flex gap-2 flex-wrap">
          <MultiSelectFilter
            options={statusOptions}
            selectedValues={statusFilter}
            onChange={(v) => {
              setStatusFilter(v);
              setCurrentPage(1);
            }}
            label="Status"
          />
          <SearchBar
            search={search}
            setSearch={setSearch}
            setCurrentPage={setCurrentPage}
          />
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-bordered align-middle">
          <thead className="table-light border">
            <tr>
              <th>#</th>
              {columns.map((col) => (
                <th key={col.header}>{col.header}</th>
              ))}
              <th>Status</th>
            </tr>
          </thead>
          <tbody className="border">
            {loading ? (
              <tr>
                <td colSpan={colSpan} className="text-center py-3">
                  Loading...
                </td>
              </tr>
            ) : data.length > 0 ? (
              data.map((row, idx) => (
                <tr key={row._id || idx} className="border">
                  <td className="border">
                    {String(startIdx + idx + 1).padStart(2, "0")}
                  </td>
                  {columns.map((col) => (
                    <td
                      key={col.header}
                      className="border"
                      style={col.preWrap ? { whiteSpace: "pre-line" } : undefined}
                    >
                      {col.render(row)}
                    </td>
                  ))}
                  <td className="border">
                    <StatusPill status={row?.status} />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={colSpan} className="text-center text-muted py-3">
                  No {emptyLabel} found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        perPage={perPage}
        setPerPage={setPerPage}
        setCurrentPage={setCurrentPage}
        currentPage={currentPage}
        totalPages={totalPages}
        totalResults={totalResults}
      />

      <style>{`
        .admin-company-table table {
          border-radius: 12px;
          border: 1px solid #e5e7eb;
        }
        .admin-company-table thead th {
          background-color: #f9fafb;
          font-weight: 600;
          color: #4B5675;
        }
        .admin-company-table tbody tr:hover {
          background-color: #f3f4f6;
        }
      `}</style>
    </div>
  );
}
